import {
  GHL_EVENTS_ENDPOINT,
  GHL_LOCATION_ID,
  GHL_TRACKING_ID,
} from "@/config/ghl";

// Client-side helper for GoHighLevel's external-tracking form endpoint. Kept
// out of the components so every form posts an identically-shaped event and a
// second form cannot drift from the first.
//
// Browser-only: it reads window/navigator/crypto. Call it from an event handler
// in a "use client" component, never during render or on the server.

export type GhlFormEvent = {
  /** Shown as the form name in GHL. */
  formId: string;
  /** field key -> submitted value. Built-ins: first_name, last_name, email, phone. */
  formData: Record<string, string>;
  /** field key -> human label, so the GHL UI shows readable column names. */
  formLabels: Record<string, string>;
};

/**
 * Post a form submission to GoHighLevel.
 *
 * FIRE-AND-FORGET BY DESIGN: it never throws and never blocks the caller. CRM
 * capture failing must not cost the user their message — the contact form's
 * real delivery path is the support inbox, and a visitor who sees "something
 * went wrong" because a marketing pixel was blocked would simply leave. Ad
 * blockers and privacy extensions block this endpoint routinely, so failure
 * here is expected traffic, not an incident.
 */
export function trackGhlFormSubmission(event: GhlFormEvent): void {
  if (typeof window === "undefined") return;

  try {
    const payload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: event.formId,
      formData: event.formData,
      formLabels: event.formLabels,
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: GHL_TRACKING_ID,
      locationId: GHL_LOCATION_ID,
      // randomUUID needs a secure context; http://localhost counts, but guard
      // anyway so an insecure preview origin cannot throw away the submission.
      sessionId:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
      },
    };

    void fetch(GHL_EVENTS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(payload),
      // The user may navigate away the moment they hit submit; keepalive lets
      // the request outlive the page instead of being cancelled mid-flight.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never let CRM bookkeeping surface as a user-visible error.
  }
}
